import { logInfo } from 'setup/logger/logInfo';
import type { ServiceWorkerHealthReport } from 'shared/domain/messages/serviceWorkerHealth';
import { frontendVersionWithBuildNumber } from 'shared/version';
import { activateWaitingServiceWorker } from './activateWaitingServiceWorker';
import { isServiceWorkerAlignmentFailed } from './model';
import { type AlignmentAttempt, type AlignmentFailure, AlignmentStage, ServiceWorkerAlignment } from './types';
import { requestServiceWorkerHealthReport, ServiceWorkerUnavailable } from '../diagnostics/requestServiceWorkerHealthReport';
import { getServiceWorkerContainer } from '../serviceWorkerContainer';

/** How long the page waits before it gives up and asks for a refresh. */
export const SERVICE_WORKER_ALIGNMENT_TIMEOUT_MS = 15_000;
/**
 * The health report answers at once; an older worker without it does not
 * answer at all. A busy worker of the page's version is asked again once the
 * server turns out to have nothing newer.
 */
const HEALTH_REPORT_TIMEOUT_MS = 2_000;

/** What `align` is doing now, kept outside it: when time runs out, it is all that is known. */
interface AlignmentProgress {
  stage: AlignmentStage;
  activeVersion?: string | null;
  /** The worker told to take over, and since when. */
  takeOver?: { worker: ServiceWorker; startedAt: number };
}

function isHealthReport(
  report: ServiceWorkerHealthReport | ServiceWorkerUnavailable
): report is ServiceWorkerHealthReport {
  return typeof report === 'object';
}

/**
 * The newest worker after the active one: an installing one was fetched after
 * the waiting one, which can be left from an earlier deploy.
 */
function findNewerWorker(registration: ServiceWorkerRegistration): ServiceWorker | null {
  return registration.installing ?? registration.waiting;
}

/** `null`: no active worker answered. */
async function readActiveWorkerVersion(timeoutMs: number): Promise<string | null> {
  const report = await requestServiceWorkerHealthReport(timeoutMs);
  return isHealthReport(report) ? report.swVersion : null;
}

/** What a failed alignment saw, as it stands now: the worker told to take over matters only while it was waited for. */
function describeFailure(progress: AlignmentProgress, error: unknown): AlignmentFailure {
  const { stage, activeVersion, takeOver } = progress;
  return {
    stage,
    activeVersion,
    error,
    ...(stage === AlignmentStage.takingOver &&
      takeOver && { takeOver: { waitedMs: Date.now() - takeOver.startedAt, workerState: takeOver.worker.state } }),
  };
}

async function align(
  registration: ServiceWorkerRegistration,
  timeoutMs: number,
  progress: AlignmentProgress
): Promise<ServiceWorkerAlignment> {
  const startedAt = Date.now();
  const remainingMs = (): number => Math.max(timeoutMs - (Date.now() - startedAt), 0);
  let hasActivatedNewWorker = false;
  let isUpdateChecked = false;
  const alignedAs = (): ServiceWorkerAlignment =>
    hasActivatedNewWorker ? ServiceWorkerAlignment.activatedNewWorker : ServiceWorkerAlignment.aligned;

  for (;;) {
    const newerWorker = findNewerWorker(registration);
    if (newerWorker) {
      if (
        !(await activateWaitingServiceWorker(newerWorker, remainingMs(), (stage) => {
          progress.stage = stage;
          if (stage === AlignmentStage.takingOver) {
            progress.takeOver = { worker: newerWorker, startedAt: Date.now() };
          }
        }))
      ) {
        return ServiceWorkerAlignment.failed;
      }
      // Newer is not always the page's version (a waiting one can be left from
      // an earlier deploy): the loop asks the one now active.
      hasActivatedNewWorker = true;
      continue;
    }
    if (!registration.active) {
      return hasActivatedNewWorker ? ServiceWorkerAlignment.failed : ServiceWorkerAlignment.noWorker;
    }
    // After the update check nothing newer will come: a worker that does not
    // answer may run the page's version and only be busy, so it gets the time left.
    progress.stage = AlignmentStage.readingVersion;
    const activeVersion = await readActiveWorkerVersion(
      isUpdateChecked ? remainingMs() : Math.min(HEALTH_REPORT_TIMEOUT_MS, remainingMs())
    );
    progress.activeVersion = activeVersion;
    if (activeVersion === frontendVersionWithBuildNumber) {
      return alignedAs();
    }
    if (!isUpdateChecked) {
      // An older worker (other version, or one without the health report): fetch the page's one.
      progress.stage = AlignmentStage.checkingForUpdate;
      await registration.update();
      isUpdateChecked = true;
      continue;
    }
    return ServiceWorkerAlignment.failed;
  }
}

/**
 * Makes the worker run the page's version before the page talks to it, so
 * neither has to understand an older protocol. Runs once at start: a newer
 * worker found (installing, waiting or after an update check) takes over at
 * once, until the active one reports the page's version. Resolves `failed`
 * after `timeoutMs` instead of starting on an old worker, with where it
 * stood; reporting the failure is the caller's, once the recovery ended.
 */
export async function alignServiceWorkerWithPage(
  timeoutMs = SERVICE_WORKER_ALIGNMENT_TIMEOUT_MS
): Promise<AlignmentAttempt> {
  const container = getServiceWorkerContainer();
  const registration = await container?.getRegistration().catch(() => undefined);
  if (!registration) {
    return { alignment: ServiceWorkerAlignment.noWorker, durationMs: 0 };
  }

  const startedAt = Date.now();
  const progress: AlignmentProgress = { stage: AlignmentStage.readingVersion };
  let timer: ReturnType<typeof setTimeout> | undefined;
  let alignmentError: unknown;
  const timedOut = new Promise<ServiceWorkerAlignment>((resolve) => {
    timer = setTimeout(() => resolve(ServiceWorkerAlignment.failed), timeoutMs);
  });
  const alignment = await Promise.race([
    align(registration, timeoutMs, progress).catch((error: unknown) => {
      alignmentError = error;
      return ServiceWorkerAlignment.failed;
    }),
    timedOut,
  ]);
  clearTimeout(timer);

  const durationMs = Date.now() - startedAt;
  if (alignment === ServiceWorkerAlignment.activatedNewWorker) {
    logInfo('service-worker-aligned-with-page', { alignment, pageVersion: frontendVersionWithBuildNumber, durationMs });
  }
  return isServiceWorkerAlignmentFailed(alignment)
    ? { alignment, durationMs, failure: describeFailure(progress, alignmentError) }
    : { alignment, durationMs };
}
